import type { Id } from "convex/_generated/dataModel";
import { LESSON_TYPE_OPTIONS, type LessonTypeArg } from "@/lib/prompts";

export type TemplateScope = "default" | Exclude<LessonTypeArg, "auto">;
export type TemplateSourceKind = "image" | "pdf" | "docx";

export type TemplateStage = {
  stage: string;
  typicalTimingMinutes?: number;
  typicalInteraction?: string;
  purpose?: string;
  teacherAction?: string;
  studentAction?: string;
  assessmentStrategy?: string;
};

export type TemplateStyleProfile = {
  stageSequence: TemplateStage[];
  tone: string;
  procedureDetailLevel: "short" | "medium" | "long";
  interactionPatterns: string[];
  sampleProcedure: string;
  writingStyleSummary: string;
  teachingApproach?: string;
  differentiationApproach?: string;
  assessmentApproach?: string;
};

export type SavedTemplate = {
  _id: Id<"user_templates">;
  name: string;
  scope: TemplateScope;
  scopeLabel: string;
  sourceMimeType: string;
  sourcePreviewUrls: string[];
  sourceMissing: boolean;
  sourceKind: TemplateSourceKind;
  pagesAnalyzed: number;
  styleProfile: TemplateStyleProfile;
  detectedLessonType?: Exclude<LessonTypeArg, "auto">;
  isDefault: boolean;
  createdAt: number;
  updatedAt: number;
  usageCount: number;
  lastUsedAt?: number;
};

export const TEMPLATE_IMAGE_MAX_BYTES = 3 * 1024 * 1024;
export const TEMPLATE_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
export const TEMPLATE_MAX_COUNT = 12;
export const TEMPLATE_MAX_IMAGES = 3;
export const TEMPLATE_MAX_PDF_PAGES = 2;

export const TEMPLATE_SCOPE_OPTIONS: { value: TemplateScope; label: string }[] = [
  { value: "default", label: "All lesson types" },
  ...LESSON_TYPE_OPTIONS.filter((option) => option.value !== "auto").map((option) => ({
    value: option.value as Exclude<LessonTypeArg, "auto">,
    label: option.label,
  })),
];

export function scopeLabel(scope: TemplateScope): string {
  return TEMPLATE_SCOPE_OPTIONS.find((option) => option.value === scope)?.label ?? scope;
}

export function templateBehaviorSummary(template: Pick<SavedTemplate, "styleProfile">): string {
  const profile = template.styleProfile;
  const timed = profile.stageSequence.some((stage) => stage.typicalTimingMinutes !== undefined);
  const interaction = profile.interactionPatterns.slice(0, 2).join(" and ");
  const parts = [
    `${profile.stageSequence.length} ${timed ? "timed " : ""}lesson stages`,
    `${profile.procedureDetailLevel} procedures`,
    interaction ? `${interaction} interaction` : null,
  ].filter(Boolean);
  return `Uses ${parts.join(", ")}.`;
}

export function formatTemplateDate(value: number): string {
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(value);
}
